import type { ResultOf } from '@graphql-typed-document-node/core'
import { useCallback } from 'react'
import type { MouseEvent } from 'react'
import { RestaurantPhotos } from '~/features/photos/RestaurantPhotos.frag'
import { getFragmentData } from '~/lib/graphql'
import type { FragmentType } from '~/lib/graphql'
import type { RestaurantId } from '~/lib/graphql/scalars'
import { Link } from '~/navigation'
import { sva } from '~/styled-system/css'
import { center, linkOverlay } from '~/styled-system/patterns'
import { RestaurantMenusFragment } from './menus/query'
import type { RestaurantRating } from './restaurantRating.frag'

type Props = {
  restaurantId: RestaurantId
  rating: ResultOf<typeof RestaurantRating>['rating']
  restaurant: FragmentType<typeof RestaurantMenusFragment> & FragmentType<typeof RestaurantPhotos>
}

const styles = sva({
  slots: ['nav', 'ul', 'li', 'a'],
  base: {
    nav: {
      width: 'full',
      shadow: 'bottom',
      _pc: {
        backgroundColor: 'gray.100',
        shadow: 'none',
      },
    },
    ul: {
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'space-around',
      _pc: {
        mx: 'auto',
        width: 'content.base',
      },
    },
    li: {
      // linkOverlay のための relative
      position: 'relative',
      p: '3',
      _pc: {
        w: 'full',
        py: '5',
        px: '0',
        _notLast: {
          '& > a': {
            borderRightWidth: 'thin',
            borderStyle: 'solid',
            borderColor: 'gray.300',
          },
        },
      },
    },
    a: linkOverlay.raw({ _pc: center.raw() }),
  },
})

export function GuideNav({ restaurantId, rating, ...props }: Props) {
  const { images } = getFragmentData(RestaurantPhotos, props.restaurant)
  const { menus } = getFragmentData(RestaurantMenusFragment, props.restaurant)
  const classes = styles()
  return (
    <nav className={classes.nav}>
      <ul className={classes.ul}>
        {!menus && (
          <li className={classes.li}>
            <PlanListLink className={classes.a} />
          </li>
        )}
        <li className={classes.li}>
          <Link
            to={`/${restaurantId}/detail`}
            preventScrollReset={true}
            prefetch="viewport"
            className={classes.a}
          >
            店舗情報
          </Link>
        </li>
        {menus && (
          <li className={classes.li}>
            <Link
              to={`/${restaurantId}/menus`}
              preventScrollReset={true}
              prefetch="viewport"
              className={classes.a}
            >
              メニュー
            </Link>
          </li>
        )}
        <li className={classes.li}>
          <Link
            to={`/${restaurantId}/access`}
            preventScrollReset={true}
            prefetch="viewport"
            className={classes.a}
          >
            アクセス
          </Link>
        </li>
        {images.length > 0 && (
          <li className={classes.li}>
            <Link
              to={`/${restaurantId}/photos`}
              preventScrollReset={true}
              prefetch="viewport"
              className={classes.a}
            >
              写真
            </Link>
          </li>
        )}
        {rating && (
          <li className={classes.li}>
            <Link
              to={`/${restaurantId}/reviews`}
              preventScrollReset={true}
              prefetch="viewport"
              className={classes.a}
            >
              クチコミ
            </Link>
          </li>
        )}
      </ul>
    </nav>
  )
}

function PlanListLink({ className }: { className?: string }) {
  const moveToMenu = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  return (
    <a href="#plans" className={className} onClick={moveToMenu}>
      予約
    </a>
  )
}
